import {PaymentReceivedDto} from "./payment-received.dto";
import {CustomerPaymentAllocationDto} from "./customer-payment-allocation.dto";
import {CustomerPaymentPlannedDto} from "./customer-payment-planned.dto";
import {ContractPaymentAllocationDto} from "./contract-payment-allocation.dto";
import {ContractPaymentPlannedDto} from "./contract-payment-planned.dto";
import {ContractDto} from "../../../../../feature-crm/cm/model/dto/entity/contract.dto";
import {TreeNode as PrimeNode} from "primeng/api";

export interface PaymentReceivedNodeMap {
    PaymentReceived:        PaymentReceivedDto;
    CustomerAllocation:     CustomerPaymentAllocationDto;
    CustomerPlanned:        CustomerPaymentPlannedDto;
    ContractAllocPlanned:   ContractPaymentAllocationDto;
    ContractPlanned:        ContractPaymentPlannedDto;
    Contract:               ContractDto;
}

// 2. Define the “next” link in the chain
type Next<K extends keyof PaymentReceivedNodeMap> =
K extends "PaymentReceived"      ? "CustomerAllocation"     :
K extends "CustomerAllocation"   ? "CustomerPlanned"        :
K extends "CustomerPlanned"      ? "ContractAllocPlanned"   :
K extends "ContractAllocPlanned" ? "ContractPlanned"        :
K extends "ContractPlanned"      ? "Contract"               :
/* leaf */                                    never;

// 3. A generic tree node
interface TreeNode<K extends keyof PaymentReceivedNodeMap> {
    data:     Partial<PaymentReceivedNodeMap[K]>;
    main:     boolean;
    children: Next<K> extends never
        ? []                        // no children for leaves
        : TreeNode<Next<K>>[];      // recurse into the next K
}

// 4. Type aliases for convenience
export type PaymentReceivedNodeDto  = TreeNode<"PaymentReceived">;


const NEXT_LEVEL: Record<keyof PaymentReceivedNodeMap, keyof PaymentReceivedNodeMap | null> = {
    PaymentReceived:      'CustomerAllocation',
    CustomerAllocation:   'CustomerPlanned',
    CustomerPlanned:      'ContractAllocPlanned',
    ContractAllocPlanned: 'ContractPlanned',
    ContractPlanned:      'Contract',
    Contract:             null
};

/**
 * Recursive mapper.  K is guaranteed to be one of the keys in PaymentReceivedNodeMap,
 * so TypeScript will ensure your `children` array matches the Next<K> type.
 */
function mapNode<K extends keyof PaymentReceivedNodeMap>(
    node: TreeNode<K>,
    level: K
): PrimeNode {
    // figure out what the next level key is
    const next = NEXT_LEVEL[level] as Next<K> | null;

    return {
        label:      'Payment Received Trace (Id)',
        data:       node.data,
        expanded:   true,
        children:   next
            ? (node.children as TreeNode<Next<K>>[])
                .map(child => mapNode(child, next!))
            : []
    };
}

/**
 * Entry point: feed your PaymentReceivedNodeDto[] in here
 * and get back TreeNode[] for p-organizationChart.
 */
export function mapPaymentReceivedTree(
    raw: PaymentReceivedNodeDto[]
): PrimeNode[] {
    return raw.map(root => mapNode(root, 'PaymentReceived'));
}
